import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { AlertTriangle, Loader, Square } from 'lucide-react';
import { toast } from 'sonner';
import { QUEUE_LABELS, formatDateTime, formatDateTimeWithRelative, messages, type JobRun, type QueueName } from '@nesc/shared';
import { api, errorMessage, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { usePollingInterval } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog, EmptyState, JobProgress } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { cn, tabTriggerClass } from '@/lib/utils';

const TABS = [
  { value: 'running,queued', label: messages.jobs.tabRunning },
  { value: 'succeeded,partial,failed,canceled', label: messages.jobs.tabFinished },
];

/** S90 実行状況 */
export const JobsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(TABS[0].value);
  const [cancelTarget, setCancelTarget] = useState<JobRun | null>(null);

  const query = { status: tab, limit: 50 };

  const { data, isLoading } = useQuery({
    queryKey: qk.jobs(query),
    queryFn: () => api.get<ListResponse<JobRun>>('/jobs', query),
    refetchInterval: usePollingInterval(tab.includes('running'), 3000),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/cancel`),
    onSuccess: () => {
      toast.success('停止を受け付けました', { description: messages.jobs.confirmCancel });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setCancelTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const stalled = (data?.data ?? []).filter((job) => job.isStalled);

  return (
    <>
      <PageHeader back="/" title={messages.jobs.title} />

      {stalled.length > 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-card border-2 border-foreground bg-muted p-3 text-body font-semibold">
          <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0" />
          {messages.jobs.stalled}（{stalled.length} 件）
        </p>
      )}

      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="mb-3 flex gap-1 border-b border-border" aria-label="処理の状態">
          {TABS.map((item) => (
            <Tabs.Trigger
              key={item.value}
              value={item.value}
              className={tabTriggerClass}
            >
              {item.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value={tab}>
          {isLoading ? (
            <SkeletonRows rows={4} />
          ) : !data || data.data.length === 0 ? (
            <EmptyState
              title={messages.empty.jobs.title}
              description={messages.empty.jobs.description}
              icon={<Loader className="size-8" />}
            />
          ) : (
            <ul className="space-y-2">
              {data.data.map((job) => (
                <li key={job.id}>
                  <Card className={cn(job.isStalled && 'border-2 border-foreground')}>
                    <CardContent className="space-y-2 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="flex flex-wrap items-center gap-2">
                            <span className="min-w-0 truncate text-body font-semibold">
                              {job.targetLabel ?? job.jobLabel}
                            </span>
                            <StatusBadge kind="job_status" value={job.status} />
                          </p>
                          <p className="text-note text-foreground-muted">
                            {QUEUE_LABELS[job.queue as QueueName] ?? job.queue}・{job.jobLabel}
                            {job.createdBy?.name && `・${messages.jobs.startedBy} ${job.createdBy.name}`}
                          </p>
                        </div>
                      </div>

                      {job.progressTotal > 0 && (
                        <JobProgress
                          label={messages.jobs.progressOf(job.progressDone, job.progressTotal)}
                          done={job.progressDone}
                          total={job.progressTotal}
                          status={job.status}
                        />
                      )}

                      <p className="tabular text-note text-foreground-subtle">
                        {messages.jobs.startedAt} {formatDateTime(job.startedAt ?? job.createdAt)}
                        {job.finishedAt && ` ・ 完了 ${formatDateTime(job.finishedAt)}`}
                        {job.errorCount > 0 && ` ・ エラー ${job.errorCount}`}
                      </p>

                      {job.isStalled && (
                        <p className="text-note font-semibold">
                          30 分以上更新がありません（最終更新 {formatDateTimeWithRelative(job.updatedAt)}）
                        </p>
                      )}

                      {job.resultSummary && Object.keys(job.resultSummary).length > 0 && (
                        <details className="text-note text-foreground-muted">
                          <summary className="cursor-pointer">結果の内訳</summary>
                          <dl className="mt-1 space-y-0.5 pl-3">
                            {Object.entries(job.resultSummary).map(([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <dt>{key}</dt>
                                <dd className="tabular">{String(value)}</dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      )}

                      <div className="flex flex-wrap gap-2 border-t border-border pt-2">
                        {job.href && (
                          <Button variant="outline" size="sm" onClick={() => navigate(job.href!)}>
                            {messages.common.detail}
                          </Button>
                        )}
                        {job.canCancel && (
                          <Button variant="outline" size="sm" onClick={() => setCancelTarget(job)}>
                            <Square aria-hidden className="size-4" />
                            {messages.jobs.cancel}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </Tabs.Content>
      </Tabs.Root>

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title={messages.jobs.cancel}
        summary={`「${cancelTarget?.targetLabel ?? cancelTarget?.jobLabel}」を停止します。`}
        detail={<p className="text-body text-foreground-muted">{messages.jobs.confirmCancel}</p>}
        confirmText={messages.jobs.cancel}
        onConfirm={() => cancelTarget && cancel.mutate(cancelTarget.id)}
        isPending={cancel.isPending}
      />
    </>
  );
};
